import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import { InputWithLabelAndError as Input } from "components/ui/InputWithLabelAndError";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";
import { login } from "helpers/apiClient";
import loadUserProfile from "./loadUserProfile";
import { widgetData } from "data/chartData/frontLayout";
import { frontLayouts } from "data/frontLayouts";
import { getRolePermissions } from "data/rolePermissions";

const LoginFrame = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [responseMessage, setResponseMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { handleLogin, setAuthUserId } = useAuth();
  const { setUserProfile, userProfile, setActiveComponent, setIsLoadingUserPermissions } = useStateContext();

  const validate = () => {
    const nextErrors = {};
    if (!identifier) nextErrors.identifier = "El email o teléfono es necesario";
    else if (!/\S+@\S+\.\S+/.test(identifier) && !/^\+[1-9][0-9]{7,14}$/.test(identifier)) {
      nextErrors.identifier = "El email o teléfono E.164 no es válido";
    }
    if (!password) nextErrors.password = "Se necesita contraseña.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setResponseMessage("");
    try {
      const session = await login(
        /\S+@\S+\.\S+/.test(identifier)
          ? { email: identifier, password }
          : { phone: identifier, password }
      );
      const profile = await loadUserProfile(setUserProfile, userProfile, setIsLoadingUserPermissions);
      setAuthUserId(profile.userId);
      handleLogin({ ...session, userId: profile.userId });
      setSuccessMessage("Sesión iniciada correctamente");
      setTimeout(() => {
        setActiveComponent("dashboard");
        navigate("/", { replace: true });
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);
      setResponseMessage(
        error.status === 409
          ? "No has activado tu cuenta. Revisa tu correo electrónico."
          : error.message || "Error conectando con el servidor"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGuestAccess = () => {
    // Guest preview is entirely local and deliberately has no Go API token.
    sessionStorage.setItem(
      "frontendPermissions",
      JSON.stringify(getRolePermissions("guest"))
    );
    sessionStorage.removeItem("currentProjectId");
    sessionStorage.removeItem("currentProjectRole");
    sessionStorage.removeItem("currentProjectRoleStatus");
    handleLogin();
    setUserProfile({
      role: "guest",
      isSuperadmin: false,
      currentProjectId: null,
      currentProjectName: "",
      projectRole: "guest",
      projectRoleStatus: "",
      roleStatus: "",
      isUserAGuest: true,
      profilePicture: "",
      parcela: "",
      poligono: "",
      catastro: "",
      userSettings: { darkMode: "system", fontSize: "16px", sidebarPosition: "left", layoutMode: "simple", dashboardLayout: frontLayouts.simple, widgets: widgetData, storedWidgets: [] },
      contactDetails: { firstName: "", lastName: "", workRole: "", bio: "", phone: "", contactMail: "", allowWhatsapp: true, address: {} },
    });
    setActiveComponent("dashboard");
  };

  return (
    <div className="max-w-md w-full flex flex-col gap-y-4 md:gap-y-8">
      <div>
        <h2 className="font-medium leading-7 dark:text-white">Login</h2>
        <span className="text-sm dark:text-white">Haz Login en tu cuenta</span>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Input variant="default" name="identifier" type="text" label="Email o Teléfono" placeholder="Escribe tu email o teléfono" value={identifier} onChange={(event) => setIdentifier(event.target.value)} error={errors.identifier} />
        </div>
        <div className="mb-4 relative">
          <Input variant="default" type="text" name="password" label="Contraseña" placeholder="••••••••" hasRevealPassword onChange={(event) => setPassword(event.target.value)} error={errors.password} />
        </div>
        <div className="flex text-sm justify-between my-2 font-semibold">
          <Link className="inline-block hover:underline text-center text-blue-400 dark:text-blue-500 hover:text-blue-600" onClick={() => setActiveComponent("forgotPassword")}>Olvidé mi contraseña</Link>
        </div>
        <Button type="submit" className="mt-2 w-full bg-blue-500 dark:bg-blue-500 text-white dark:text-white py-2 px-4 rounded-md hover:bg-blue-600 dark:hover:bg-blue-600">
          {loading ? <Icon icon="svg-spinners:180-ring" className="text-white" /> : "Iniciar Sesión"}
        </Button>
      </form>
      <Link className="inline-block hover:underline text-center text-blue-400 hover:text-blue-600" onClick={handleGuestAccess}>Acceder como invitado</Link>
      <div className="flex text-sm dark:text-white justify-center gap-x-4 my-2 font-semibold">
        <span>¿No tienes una cuenta?</span>
        <Link className="inline-block hover:underline text-center text-blue-400 hover:text-blue-600" onClick={() => setActiveComponent("register")}>Regístrate</Link>
      </div>
      <div className="h-4 flex flex-col justify-center items-center">
        {successMessage ? <p className="text-sm text-green-500">{successMessage}</p> : responseMessage ? <p className="text-sm text-red-500">{responseMessage}</p> : null}
      </div>
    </div>
  );
};

export default LoginFrame;
